import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, ToastController, ModalController } from '@ionic/angular';
import { ToastService } from 'app/services/toast.service';

import * as fromUser from 'app/state/user';
import { Store } from '@ngrx/store';
import * as fromTap from 'app/state/tap';
import * as fromTower from 'app/state/tower';
import * as fromCo2 from 'app/state/co2-bottle';
import * as fromFermenter from 'app/state/fermenter';
import { ITap } from 'app/models/tap';
import { ICO2BottleVM } from 'app/models/co2-bottle';
import { IFermenterVM } from 'app/models/fermenter';
import { TapsComponent } from './taps/taps.component';
import { Co2PickerComponent } from './co2/co2.component';
import { FermenterPickerComponent } from './fermenter/fermenter.component';

import { IonicModule } from '@ionic/angular';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormComponent } from './form/form.component';
import { HelpModalComponent } from 'app/components/help-modal/help-modal.component';

@Component({
  selector: 'app-tower-detail',
  templateUrl: './detail.page.html',
  styleUrls: ['./detail.page.scss'],
  standalone: true,
  imports: [
    IonicModule,
    FormsModule,
    ReactiveFormsModule,
    FormComponent
]
})
export class TowerDetailPage {
  barId = this.store.selectSignal(fromUser.selectBarId);
  uid = this.store.selectSignal(fromUser.selectUserId);
  items = this.store.selectSignal(fromTower.selectAllItems);
  item = this.store.selectSignal(fromTower.selectCurrentItem);
  allTaps = this.store.selectSignal(fromTap.selectAllItems);
  taps = this.store.selectSignal(fromTap.selectTapsBySelectedTowerId());
  co2Bottles = this.store.selectSignal(fromCo2.selectCO2BottlesBySelectedTowerId());
  fermenters = this.store.selectSignal(fromFermenter.selectFermentersBySelectedTowerId());

  constructor(
    private store: Store,
    private router: Router,
    public alertController: AlertController,
    public toastController: ToastController,
    public toastService: ToastService,
    private modalController: ModalController) { }

  tracById(index: number, item: any) {
    return item.id;
  }

  softUpdate(tower: any) {
    this.store.dispatch(fromTower.softUpdate({ id: tower.id, data: tower.data, uid: tower.uid }));
  }


  async confirmAlert(id: string) {
    const alert = await this.alertController.create({
      header: 'Confirm',
      message: 'Are you sure you want to delete?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary',
        },
        {
          text: 'Delete',
          handler: () => {
            this.delete(id);
          },
        },
      ],
    });

    await alert.present();
  }


  private async delete(id: string) {
    this.taps().forEach(tap => {
      this.removeTap(tap);
    });
    this.co2Bottles().forEach(co2 => {
      this.removeCo2(co2);
    });
    this.fermenters().forEach(fermenter => {
      this.removeFermenter(fermenter);
    });

    this.store.dispatch(fromTower.deleteItem({ id }));
    this.router.navigate(['inventory/towers']);
  }

  //#region Taps

  async openAddTapModal() {
    const modal = await this.modalController.create({
      component: TapsComponent,
      componentProps: {}
    });

    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        const selectedTap = data.data as ITap;
        await this.addTapToTower(selectedTap);
      }
    });

    return await modal.present();
  }

  private async addTapToTower(tap: ITap) {
    const currentTower = this.item();
    if (tap.id && currentTower?.id) {
      const update: Partial<ITap> = { towerId: currentTower.id };
      this.store.dispatch(fromTap.softUpdate({
        id: tap.id,
        data: update,
        uid: this.uid()!
      }));
    }
  }

  async confirmDeleteTap(item: ITap) {
    const alert = await this.alertController.create({
      header: 'Confirm',
      message: 'Are you sure you want to delete?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          cssClass: 'secondary'
        }, {
          text: 'Delete',
          handler: () => {
            this.removeTap(item);
          }
        }
      ]
    });

    await alert.present();
  }

  async removeTap(tap: any) {
    var update: Partial<ITap> = { towerId: '' };
    this.store.dispatch(fromTap.softUpdate({ id: tap.id!, data: update, uid: this.uid()! }));
  }

  //#endregion Taps


  public async openHelpModal() {
    const modal = await this.modalController.create({
      component: HelpModalComponent,
      componentProps: {
        component: 'TowerDetailPage'
      }
    });
    return await modal.present();
  }


  //#region CO2

  async openAddCo2Modal() {
    const modal = await this.modalController.create({
      component: Co2PickerComponent,
      componentProps: {}
    });

    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        const selected = data.data as ICO2BottleVM;
        await this.addCo2ToTower(selected);
      }
    });

    return await modal.present();
  }

  private async addCo2ToTower(co2: ICO2BottleVM) {
    const currentTower = this.item();
    if (co2.id && currentTower?.id) {
      this.store.dispatch(fromCo2.softUpdate({
        id: co2.id,
        data: { towerId: currentTower.id },
        uid: this.uid()!
      }));
    }
  }

  async confirmDeleteCo2(item: ICO2BottleVM) {
    const alert = await this.alertController.create({
      header: 'Confirm',
      message: 'Are you sure you want to remove this CO2 bottle from the tower?',
      buttons: [
        { text: 'Cancel', role: 'cancel', cssClass: 'secondary' },
        { text: 'Remove', handler: () => { this.removeCo2(item); } }
      ]
    });

    await alert.present();
  }

  async removeCo2(co2: any) {
    this.store.dispatch(fromCo2.softUpdate({ id: co2.id!, data: { towerId: '' }, uid: this.uid()! }));
  }

  //#endregion CO2


  //#region Fermenters

  async openAddFermenterModal() {
    const modal = await this.modalController.create({
      component: FermenterPickerComponent,
      componentProps: {}
    });

    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        const selected = data.data as IFermenterVM;
        await this.addFermenterToTower(selected);
      }
    });

    return await modal.present();
  }

  private async addFermenterToTower(fermenter: IFermenterVM) {
    const currentTower = this.item();
    if (fermenter.id && currentTower?.id) {
      this.store.dispatch(fromFermenter.softUpdate({
        id: fermenter.id,
        data: { towerId: currentTower.id },
        uid: this.uid()!
      }));
    }
  }

  async confirmDeleteFermenter(item: IFermenterVM) {
    const alert = await this.alertController.create({
      header: 'Confirm',
      message: 'Are you sure you want to remove this fermenter from the tower?',
      buttons: [
        { text: 'Cancel', role: 'cancel', cssClass: 'secondary' },
        { text: 'Remove', handler: () => { this.removeFermenter(item); } }
      ]
    });

    await alert.present();
  }

  async removeFermenter(fermenter: any) {
    this.store.dispatch(fromFermenter.softUpdate({ id: fermenter.id!, data: { towerId: '' }, uid: this.uid()! }));
  }

  //#endregion Fermenters

}
